import React, { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { PlayableItem } from '../domain/types';
import { logger } from '../infrastructure/logger';

interface AudioContextType {
    isPlaying: boolean;
    isPaused: boolean;
    currentItem: PlayableItem | null;
    rate: number;
    micPermission: PermissionState | 'unsupported';
    play: (item: PlayableItem) => void;
    pause: () => void;
    resume: () => void;
    stop: () => void;
    setPlaybackRate: (rate: number) => void;
    requestMicPermission: () => Promise<boolean>;
    availableVoices: SpeechSynthesisVoice[];
}

const AudioReactContext = createContext<AudioContextType | null>(null);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentItem, setCurrentItem] = useState<PlayableItem | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [rate, setRate] = useState(1.0);
    const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([]);
    const [micPermission, setMicPermission] = useState<PermissionState | 'unsupported'>('prompt');
    
    const speechRef = useRef<SpeechSynthesisUtterance | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);

    // 1. Monitor Permission Status
    useEffect(() => {
        if (navigator.permissions && navigator.permissions.query) {
            navigator.permissions.query({ name: 'microphone' as PermissionName })
                .then((permissionStatus) => {
                    setMicPermission(permissionStatus.state);
                    permissionStatus.onchange = () => {
                        setMicPermission(permissionStatus.state);
                    };
                })
                .catch(() => setMicPermission('unsupported'));
        } else {
            setMicPermission('unsupported');
        }
    }, []);

    // 2. Load Voices
    const loadVoices = useCallback(() => {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
            setAvailableVoices(voices);
        }
    }, []);

    useEffect(() => {
        loadVoices();
        window.speechSynthesis.onvoiceschanged = loadVoices;
        return () => { window.speechSynthesis.onvoiceschanged = null; };
    }, [loadVoices]);

    // 3. Multimedia Nudge: Force the OS to use Media channel instead of Communication channel
    const nudgeAudioChannel = async () => {
        try {
            if (!audioContextRef.current) {
                const AudioContextClass = window.AudioContext || 
                    (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
                if (AudioContextClass) {
                    audioContextRef.current = new AudioContextClass();
                } else {
                    logger.warn("Web Audio API not supported");
                    return;
                }
            }
            if (audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
            }
            // Play a tiny bit of silence on the media channel
            const oscillator = audioContextRef.current.createOscillator();
            const gainNode = audioContextRef.current.createGain();
            gainNode.gain.value = 0.0001; // Inaudible
            oscillator.connect(gainNode);
            gainNode.connect(audioContextRef.current.destination);
            oscillator.start();
            oscillator.stop(audioContextRef.current.currentTime + 0.001);
            
            // Clean up nodes immediately to prevent memory pressure
            setTimeout(() => {
                oscillator.disconnect();
                gainNode.disconnect();
            }, 100);
        } catch (e) {
            const error = e instanceof Error ? e : new Error(String(e));
            logger.warn("Nudge failed, proceeding with standard speech", { error: error.message }, error);
        }
    };

    const getBestVoice = useCallback(() => {
        const esVoices = availableVoices.filter(v => v.lang.startsWith('es'));
        if (esVoices.length === 0) return null;
        const highQualityMale = esVoices.find(v => 
            v.name.includes('Pablo') || v.name.includes('Jorge') || 
            v.name.includes('Diego') || v.name.includes('Miguel')
        );
        return highQualityMale || esVoices.find(v => v.name.includes('Google') && !v.name.includes('Female')) || esVoices[0];
    }, [availableVoices]);

    const startSpeaking = async (item: PlayableItem) => {
        window.speechSynthesis.cancel();
        
        // Step A: Trigger nudge to switch to Multimedia Profile (Speakers)
        await nudgeAudioChannel();

        const textToRead = `${item.title}. ${item.contentToRead}`;
        const utterance = new SpeechSynthesisUtterance(textToRead);
        
        const voice = getBestVoice();
        if (voice) utterance.voice = voice;
        
        utterance.lang = 'es-ES';
        utterance.rate = rate;
        utterance.pitch = 1.0; // Standard pitch for media feel
        utterance.volume = 1.0; // Max volume for normalization

        utterance.onstart = () => {
            setIsPlaying(true);
            setIsPaused(false);
        };
        utterance.onend = () => {
            setIsPlaying(false);
            setIsPaused(false);
        };
        utterance.onerror = (e: { error?: string; message?: string }) => {
            const error = new Error(String(e.error || e.message || 'Speech error'));
            logger.error("Speech error", error);
            setIsPlaying(false);
        };

        speechRef.current = utterance;
        setCurrentItem(item);
        window.speechSynthesis.speak(utterance);
    };

    const requestMicPermission = async (): Promise<boolean> => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // CRITICAL: Stop all tracks immediately so the system releases the "Communication" state
            stream.getTracks().forEach(track => {
                track.stop();
                track.enabled = false;
            });
            setMicPermission('granted');
            // Re-nudge to ensure we return to multimedia output
            await nudgeAudioChannel();
            return true;
        } catch (err) {
            logger.error("Permission denied", err instanceof Error ? err : new Error(String(err)));
            setMicPermission('denied');
            return false;
        }
    };

    const play = useCallback((item: PlayableItem) => {
        if (currentItem?.id === item.id) {
            if (window.speechSynthesis.paused) {
                window.speechSynthesis.resume();
                setIsPaused(false);
                setIsPlaying(true);
            } else if (window.speechSynthesis.speaking) {
                window.speechSynthesis.pause();
                setIsPaused(true);
                setIsPlaying(false);
            } else {
                startSpeaking(item);
            }
            return;
        }
        startSpeaking(item);
    }, [currentItem, isPlaying, isPaused, rate, availableVoices, startSpeaking]);

    const pause = useCallback(() => { 
        if (window.speechSynthesis.speaking) {
            window.speechSynthesis.pause(); 
            setIsPlaying(false); 
            setIsPaused(true); 
        }
    }, []);

    const resume = useCallback(() => { 
        if (window.speechSynthesis.paused) {
            window.speechSynthesis.resume(); 
            setIsPlaying(true); 
            setIsPaused(false); 
        }
    }, []);

    const stop = useCallback(() => { 
        window.speechSynthesis.cancel(); 
        setIsPlaying(false); 
        setIsPaused(false); 
        setCurrentItem(null); 
    }, []);

    const setPlaybackRate = useCallback((newRate: number) => {
        setRate(newRate);
        if ((isPlaying || isPaused) && currentItem) {
            startSpeaking(currentItem); 
        }
    }, [isPlaying, isPaused, currentItem, rate]);

    const value = useMemo(() => ({
        isPlaying, isPaused, currentItem, rate, micPermission,
        play, pause, resume, stop, setPlaybackRate, requestMicPermission, availableVoices
    }), [isPlaying, isPaused, currentItem, rate, micPermission, play, pause, resume, stop, setPlaybackRate, requestMicPermission, availableVoices]);

    return (
        <AudioReactContext.Provider value={value}>
            {children}
        </AudioReactContext.Provider>
    );
};

export const useAudio = () => {
    const context = useContext(AudioReactContext);
    if (!context) throw new Error("useAudio must be used within AudioProvider");
    return context;
};